import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CardGiftcardIcon from "@mui/icons-material/CardGiftcard";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import LightbulbOutlinedIcon from "@mui/icons-material/LightbulbOutlined";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import MonetizationOnIcon from "@mui/icons-material/MonetizationOn";
import ShuffleIcon from "@mui/icons-material/Shuffle";
import Alert from "@mui/material/Alert";
import Badge from "@mui/material/Badge";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import Paper from "@mui/material/Paper";
import Popover from "@mui/material/Popover";
import Snackbar from "@mui/material/Snackbar";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import useMediaQuery from "@mui/material/useMediaQuery";
import { Link as RouterLink, useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import UserService from "../../../services/UserService";
import AudioService, { AUDIO_EFFECT } from "../../../services/AudioService";
import { usePagesTranslation } from "../../locales";
import WordMeaningDialog from "../common/WordMeaningDialog";
import LetterWheel, { buildWheelTiles, lettersForWheel, shuffleWheelTiles } from "./components/LetterWheel";
import ClueBanner from "./components/ClueBanner";
import TreasureBonusWordsDialog from "./components/TreasureBonusWordsDialog";
import TreasureChestIcon from "./components/TreasureChestIcon";
import WordRevealCircles from "./components/WordRevealCircles";
import WordWheelCompleteDialog from "./components/WordWheelCompleteDialog";
import WordWheelService from "./WordWheelService";
import { JOURNEY_APP_BACKGROUND_URL } from "./bgAssets";
import WordWheelWallet, {
    WORD_WHEEL_COINS_PLAN,
    WW_DAILY_GIFT_EVENT,
    collectWordWheelDailyGift,
    getWordWheelDailyGiftState,
    notifyWordWheelWalletCoins,
} from "./WordWheelWallet";
import { ShopHeroIcon, goldCtaSx, goldShopPaperSx } from "./shopUi";
import {
    WORD_WHEEL_BONUS_WORD_GIFT,
    WORD_WHEEL_COIN_HINT_COST,
    WORD_WHEEL_SEASON,
    getGuestJourneyLevel,
    guestDailyFoundKey,
    resolveWordWheelGridSize,
    setGuestJourneyLevel,
} from "./constants";
import {
    loadStoredBonusWords,
    mergeBonusWordLists,
    parseBonusWordsFromPlay,
    saveStoredBonusWords,
} from "./bonusWordsStorage";
import { addGuestPuzzleCoins, loadGuestPuzzleCoins } from "./guestCoinsStorage";
import { validateBonusWord } from "./dictionaryBonus";
import {
    buildCellWordNumbers,
    buildClueMapFromDisplayClue,
    buildDisplayGrid,
    buildWordToNumberMap,
    buildWordRevealLetters,
    findHintStartCandidates,
    findWordsAtCell,
    normalizeWord,
    parseWordPositions,
    puzzleCellKeys,
    usedPuzzleBounds,
} from "./gridReveal";
import { wordWheelPackOffer } from "./packOffers";
import { wwPlayColorsLight } from "./theme";

const CLUE_PLACEHOLDER_KEY = "wordwheel.clue-placeholder";
const WHEEL_TOOLBAR_WIDTH = 44;
const CELL_HINT_AFTER_FAILS = 3;

function parseWords(wordsInUse) {
    if (!wordsInUse) return [];
    return wordsInUse.split("\n").map((w) => w.trim()).filter(Boolean);
}

function parseSavedFoundWords(wordsFound) {
    if (!wordsFound) return [];
    const raw = Array.isArray(wordsFound) ? wordsFound : String(wordsFound).split("\n");
    return raw.map(normalizeWord).filter(Boolean);
}

function isPuzzlePlayComplete(play, puzzleData) {
    if (play?.isCompleted) return true;
    const targets = parseWords(puzzleData?.wordsInUse).map(normalizeWord);
    if (!targets.length) return false;
    const saved = parseSavedFoundWords(play?.wordsFound);
    return targets.every((word) => saved.includes(word));
}

function shiftDateIso(iso, days) {
    const d = iso ? new Date(`${iso}T12:00:00`) : new Date();
    if (Number.isNaN(d.getTime())) return iso;
    d.setDate(d.getDate() + days);
    return d.toISOString().slice(0, 10);
}

function todayIso() {
    return new Date().toLocaleDateString("en-CA", { timeZone: "America/Toronto" });
}

/** Lowest-numbered target word that is still unrevealed, or null if none. */
function firstUnrevealedWord(targetWords, foundWords, wordToNumber) {
    const remaining = (targetWords || []).filter((w) => !foundWords.includes(w));
    if (remaining.length === 0) return null;
    return remaining.slice().sort((a, b) => {
        const na = wordToNumber?.get?.(a);
        const nb = wordToNumber?.get?.(b);
        return (Number.isFinite(na) ? na : 999) - (Number.isFinite(nb) ? nb : 999);
    })[0];
}

export default function WordWheelPlayPage() {
    const landscapePlay = useMediaQuery("(orientation: landscape)");
    const { t, lang } = usePagesTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const { level: levelParam, tplId, packCode } = useParams();
    const [searchParams] = useSearchParams();
    const playColors = wwPlayColorsLight;
    const isDaily = location.pathname.includes("/daily") || searchParams.get("mode") === "daily";
    const dailyDate = isDaily ? (searchParams.get("date") || undefined) : undefined;

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [signedIn, setSignedIn] = useState(false);
    const [puzzle, setPuzzle] = useState(null);
    const [foundWords, setFoundWords] = useState([]);
    const [selectedIndices, setSelectedIndices] = useState([]);
    const [, setPlaySession] = useState(null);
    const [selectedWord, setSelectedWord] = useState(null);
    const [wheelTiles, setWheelTiles] = useState([]);
    const [completionDialogOpen, setCompletionDialogOpen] = useState(false);
    const [hintLetters, setHintLetters] = useState(() => new Map());
    const [hintPending, setHintPending] = useState(false);
    const [shuffleSignal, setShuffleSignal] = useState(0);
    const [coins, setCoins] = useState(0);
    const [buyPrompt, setBuyPrompt] = useState(null);
    const [openShopKind, setOpenShopKind] = useState(null);
    const [dictionaryOpen, setDictionaryOpen] = useState(false);
    const [dictionaryWord, setDictionaryWord] = useState("");
    const [bonusWordsFound, setBonusWordsFound] = useState([]);
    const [treasureOpen, setTreasureOpen] = useState(false);
    const [chestPulse, setChestPulse] = useState(false);
    const [giftPending, setGiftPending] = useState(() => getWordWheelDailyGiftState().pending);
    const [giftGrant, setGiftGrant] = useState(() => getWordWheelDailyGiftState().grant);
    const [giftNote, setGiftNote] = useState("");
    const [giftSnackOpen, setGiftSnackOpen] = useState(false);
    const [failCount, setFailCount] = useState(0);
    const [hintHideUntil, setHintHideUntil] = useState(CELL_HINT_AFTER_FAILS);
    const [cellHintOpen, setCellHintOpen] = useState(false);
    const [reloadNonce, setReloadNonce] = useState(0);
    const [ownsDailyBundle, setOwnsDailyBundle] = useState(false);
    const completionDialogShownRef = useRef(false);
    const bulbBtnRef = useRef(null);
    const cellHintBtnRef = useRef(null);
    const failCountRef = useRef(0);
    const bonusWordsFoundRef = useRef([]);
    const foundWordsRef = useRef([]);
    const bonusWordLookupRef = useRef(false);
    bonusWordsFoundRef.current = bonusWordsFound;
    foundWordsRef.current = foundWords;

    const targetWords = useMemo(() => parseWords(puzzle?.wordsInUse).map(normalizeWord), [puzzle]);
    const baseWheelLetters = useMemo(
        () => lettersForWheel(targetWords, puzzle?.lettersGrid, puzzle?.details),
        [targetWords, puzzle],
    );
    const wordPositions = useMemo(() => parseWordPositions(puzzle?.filledCoordinates), [puzzle]);
    const puzzleCells = useMemo(() => puzzleCellKeys(wordPositions), [wordPositions]);
    const gridSize = useMemo(() => resolveWordWheelGridSize(puzzle), [puzzle]);
    const gridBounds = useMemo(
        () => usedPuzzleBounds(puzzleCells, gridSize),
        [puzzleCells, gridSize],
    );
    const visibleRows = Math.max(1, gridBounds.maxRow - gridBounds.minRow + 1);
    const visibleCols = Math.max(1, gridBounds.maxCol - gridBounds.minCol + 1);
    const displayGrid = useMemo(
        () => buildDisplayGrid(foundWords, wordPositions, hintLetters, gridSize),
        [foundWords, wordPositions, hintLetters, gridSize],
    );
    const hintedCellKeys = useMemo(() => new Set(hintLetters.keys()), [hintLetters]);
    const hintCandidates = useMemo(
        () => findHintStartCandidates(puzzle?.filledCoordinates, foundWords, hintedCellKeys),
        [puzzle?.filledCoordinates, foundWords, hintedCellKeys],
    );
    const puzzleComplete = foundWords.length >= targetWords.length && targetWords.length > 0;
    const cellHintKeys = useMemo(() => {
        if (puzzleComplete || failCount < hintHideUntil) return new Set();
        const hiddenOf = (word) =>
            (wordPositions[word] || []).filter((p) => !displayGrid[p.row]?.[p.col]);
        const selected = normalizeWord(selectedWord || "");
        const selectedHidden = selected && !foundWords.includes(selected) ? hiddenOf(selected) : [];
        const tracked = selectedHidden.length >= 2
            ? selected
            : (targetWords.find((word) => !foundWords.includes(word) && hiddenOf(word).length >= 2) || "");
        if (!tracked) return new Set();
        const hidden = hiddenOf(tracked);
        if (hidden.length < 2) return new Set();
        const hop = Math.floor((failCount - hintHideUntil) / CELL_HINT_AFTER_FAILS);
        const cell = hidden[hop % hidden.length];
        return new Set([`${cell.row},${cell.col}`]);
    }, [
        puzzleComplete,
        failCount,
        hintHideUntil,
        selectedWord,
        foundWords,
        targetWords,
        wordPositions,
        displayGrid,
    ]);
    const cellWordNumbers = useMemo(
        () => buildCellWordNumbers(puzzle?.filledCoordinates),
        [puzzle?.filledCoordinates],
    );
    const wordToNumber = useMemo(
        () => buildWordToNumberMap(puzzle?.filledCoordinates, cellWordNumbers),
        [puzzle?.filledCoordinates, cellWordNumbers],
    );
    const clueMap = useMemo(
        () => buildClueMapFromDisplayClue(puzzle?.displayClue),
        [puzzle?.displayClue],
    );
    const selectedWordNumber = selectedWord ? wordToNumber.get(selectedWord) ?? null : null;
    const selectedClue = selectedWord ? (clueMap.get(selectedWord) || "") : "";
    const selectedWordCells = useMemo(() => {
        if (!selectedWord || !wordPositions[selectedWord]) return new Set();
        return new Set(wordPositions[selectedWord].map((p) => `${p.row},${p.col}`));
    }, [selectedWord, wordPositions]);
    const selectedWheelWord = useMemo(
        () => selectedIndices.map((i) => wheelTiles[i]?.letter || "").join(""),
        [selectedIndices, wheelTiles],
    );
    const hintOnlyCells = useMemo(() => {
        const keys = new Set();
        hintLetters.forEach((_, key) => {
            let fromFound = false;
            for (const w of foundWords) {
                const positions = wordPositions[w];
                if (positions?.some((p) => `${p.row},${p.col}` === key)) {
                    fromFound = true;
                    break;
                }
            }
            if (!fromFound) keys.add(key);
        });
        return keys;
    }, [hintLetters, foundWords, wordPositions]);

    const selectedIndicesRef = useRef(selectedIndices);
    const wheelTilesRef = useRef(wheelTiles);
    wheelTilesRef.current = wheelTiles;
    selectedIndicesRef.current = selectedIndices;

    useEffect(() => {
        setWheelTiles(buildWheelTiles(baseWheelLetters, puzzle?.id || "wheel"));
    }, [puzzle?.id, baseWheelLetters]);

    const refreshWallets = useCallback(async (loggedIn) => {
        if (!loggedIn) {
            const guestCoins = loadGuestPuzzleCoins();
            setCoins(guestCoins);
            notifyWordWheelWalletCoins(guestCoins);
            return;
        }
        try {
            const user = await UserService.getUserInfo().catch(() => null);
            const next = Number(user?.cloudUser?.puzzleCoins?.word_wheel_quest) || 0;
            setCoins(next);
            notifyWordWheelWalletCoins(next);
        } catch {
            /* ignore */
        }
    }, []);

    useEffect(() => {
        const current = getWordWheelDailyGiftState();
        setGiftPending(current.pending);
        setGiftGrant(current.grant);
        const onGift = (event) => {
            setGiftPending(Boolean(event?.detail?.pending));
            const grant = Number(event?.detail?.grant);
            if (Number.isFinite(grant) && grant >= 0) setGiftGrant(grant);
        };
        window.addEventListener(WW_DAILY_GIFT_EVENT, onGift);
        return () => window.removeEventListener(WW_DAILY_GIFT_EVENT, onGift);
    }, []);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoading(true);
            setError("");
            setFoundWords([]);
            setBonusWordsFound([]);
            setHintLetters(new Map());
            setPlaySession(null);
            setFailCount(0);
            failCountRef.current = 0;
            setHintHideUntil(CELL_HINT_AFTER_FAILS);
            setCellHintOpen(false);
            completionDialogShownRef.current = false;
            setCompletionDialogOpen(false);
            try {
                const loggedIn = await WordWheelService.isLoggedIn();
                if (cancelled) return;
                setSignedIn(loggedIn);
                let owned = [];
                if (loggedIn) {
                    try {
                        owned = await WordWheelService.fetchOwnedPacks();
                    } catch {
                        owned = [];
                    }
                }
                const dailyUnlocked = owned.includes("daily_bundle");
                if (!cancelled) setOwnsDailyBundle(dailyUnlocked);
                await refreshWallets(loggedIn);

                let data;
                if (tplId) {
                    data = await WordWheelService.fetchById(tplId);
                } else if (isDaily) {
                    const requested = dailyDate || todayIso();
                    if (!dailyUnlocked && requested !== todayIso()) {
                        setError("Daily Bundle opens other days. Today stays free.");
                        return;
                    }
                    data = await WordWheelService.fetchDaily(dailyUnlocked ? dailyDate : undefined);
                } else if (packCode) {
                    data = await WordWheelService.fetchJourneyLevel(Number(levelParam) || 1, packCode);
                } else if (levelParam) {
                    data = await WordWheelService.fetchJourneyLevel(Number(levelParam), WORD_WHEEL_SEASON);
                } else if (loggedIn) {
                    data = await WordWheelService.fetchNext();
                } else {
                    data = await WordWheelService.fetchJourneyLevel(getGuestJourneyLevel(), WORD_WHEEL_SEASON);
                }
                if (data?.code === "NO_DATA" || data?.code === "FAILURE") {
                    setError(isDaily ? t("wordwheel.no-daily") : t("wordwheel.no-puzzle"));
                    return;
                }
                if (cancelled) return;
                setPuzzle(data);

                if (loggedIn && data?.id) {
                    const play = await WordWheelService.startPlay(data.id);
                    if (!cancelled && play && !play.code) {
                        setPlaySession(play);
                        const saved = parseSavedFoundWords(play.wordsFound);
                        // Completed dailies replay from an empty board; in-progress resumes.
                        setFoundWords(isDaily && isPuzzlePlayComplete(play, data) ? [] : saved);
                    }
                    if (!cancelled && data.id) {
                        const bonus = mergeBonusWordLists(
                            parseBonusWordsFromPlay(play && !play.code ? play : null),
                            loadStoredBonusWords(data.id),
                        );
                        setBonusWordsFound(bonus);
                        if (bonus.length) saveStoredBonusWords(data.id, bonus);
                    }
                } else if (!loggedIn && isDaily && data?.dailyPlayDate) {
                    try {
                        const raw = sessionStorage.getItem(guestDailyFoundKey(data.dailyPlayDate));
                        const saved = raw ? JSON.parse(raw) : [];
                        if (Array.isArray(saved)) {
                            const restored = saved.map(normalizeWord).filter(Boolean);
                            const alreadyComplete = isPuzzlePlayComplete(
                                { wordsFound: restored },
                                data
                            );
                            setFoundWords(alreadyComplete ? [] : restored);
                        }
                    } catch {
                        /* ignore */
                    }
                    if (!cancelled && data?.id) {
                        const bonus = loadStoredBonusWords(data.id);
                        setBonusWordsFound(bonus);
                    }
                } else if (!cancelled && data?.id) {
                    const bonus = loadStoredBonusWords(data.id);
                    setBonusWordsFound(bonus);
                }
            } catch (e) {
                if (!cancelled) setError(e?.message || t("wordwheel.load-failed"));
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [isDaily, dailyDate, levelParam, tplId, packCode, refreshWallets, t, reloadNonce]);

    const persistProgress = useCallback(async (words, bonusWords = bonusWordsFoundRef.current) => {
        if (!puzzle?.id || !signedIn) {
            if (!signedIn && isDaily && puzzle?.dailyPlayDate) {
                try {
                    sessionStorage.setItem(guestDailyFoundKey(puzzle.dailyPlayDate), JSON.stringify(words));
                } catch {
                    /* ignore */
                }
            }
            return null;
        }
        try {
            const updated = await WordWheelService.updateProgress(puzzle.id, words, bonusWords);
            if (updated && !updated.code) {
                setPlaySession(updated);
                if (updated.totalPuzzleCoins != null) {
                    const next = Number(updated.totalPuzzleCoins) || 0;
                    setCoins(next);
                    notifyWordWheelWalletCoins(next);
                }
                return updated;
            }
            return null;
        } catch (e) {
            setError(e?.message || t("wordwheel.save-failed"));
            return null;
        }
    }, [puzzle, signedIn, isDaily, t]);

    const applyBonusWordGift = useCallback((amount) => {
        const gift = Math.max(0, Number(amount) || 0);
        if (!gift) return;
        if (signedIn) {
            setCoins((prev) => {
                const next = prev + gift;
                notifyWordWheelWalletCoins(next);
                return next;
            });
            return;
        }
        const next = addGuestPuzzleCoins(gift);
        setCoins(next);
        notifyWordWheelWalletCoins(next);
    }, [signedIn]);

    const noteWordMiss = useCallback(() => {
        setFailCount((count) => {
            const next = count + 1;
            failCountRef.current = next;
            return next;
        });
    }, []);

    const clearDragPath = useCallback((word) => {
        setSelectedIndices((prev) => {
            if (!prev.length) return prev;
            if (!word) return [];
            const current = normalizeWord(
                prev.map((i) => wheelTilesRef.current[i]?.letter || "").join(""),
            );
            if (current && current !== word) return prev;
            return [];
        });
    }, []);

    const submitWord = useCallback(async (wordRaw) => {
        const word = normalizeWord(wordRaw);
        if (word.length < 3) {
            clearDragPath(word);
            return;
        }
        if (foundWords.includes(word)) {
            clearDragPath(word);
            return;
        }
        if (!targetWords.includes(word)) {
            if (bonusWordsFoundRef.current.includes(word)) {
                noteWordMiss();
                clearDragPath(word);
                return;
            }
            noteWordMiss();
            if (bonusWordLookupRef.current) {
                clearDragPath(word);
                return;
            }
            bonusWordLookupRef.current = true;
            try {
                const check = await validateBonusWord(word, puzzle?.language || "english");
                if (!check.ok) {
                    clearDragPath(word);
                    return;
                }
                setFailCount((count) => {
                    const next = Math.max(0, count - 1);
                    failCountRef.current = next;
                    return next;
                });
                const nextBonus = bonusWordsFoundRef.current.includes(word)
                    ? bonusWordsFoundRef.current
                    : [...bonusWordsFoundRef.current, word];
                setBonusWordsFound(nextBonus);
                if (puzzle?.id) saveStoredBonusWords(puzzle.id, nextBonus);
                const updated = await persistProgress(foundWordsRef.current, nextBonus);
                if (updated?.totalPuzzleCoins == null) {
                    applyBonusWordGift(WORD_WHEEL_BONUS_WORD_GIFT);
                }
                setChestPulse(true);
                window.setTimeout(() => setChestPulse(false), 700);
                void AudioService.play(AUDIO_EFFECT.WORD_RESOLVED);
                clearDragPath(word);
            } catch {
                clearDragPath(word);
            } finally {
                bonusWordLookupRef.current = false;
            }
            return;
        }
        const next = [...foundWords, word];
        setFoundWords(next);
        clearDragPath(word);
        // Clue follows a hidden word: if the shown word was just found, switch to another unrevealed clue.
        const selectedNorm = normalizeWord(selectedWord || "");
        if (selectedNorm === word || !selectedNorm) {
            setSelectedWord(firstUnrevealedWord(targetWords, next, wordToNumber) || word);
        }
        await persistProgress(next);
        const puzzleCompleteNow = next.length >= targetWords.length;
        if (puzzleCompleteNow && !completionDialogShownRef.current) {
            completionDialogShownRef.current = true;
            if (!signedIn && !isDaily) {
                setGuestJourneyLevel(getGuestJourneyLevel() + 1);
            }
            setCompletionDialogOpen(true);
        }
    }, [
        targetWords,
        foundWords,
        selectedWord,
        wordToNumber,
        persistProgress,
        signedIn,
        isDaily,
        puzzle,
        applyBonusWordGift,
        noteWordMiss,
        clearDragPath,
    ]);

    const handleDragEnd = useCallback(() => {
        const word = selectedIndicesRef.current
            .map((i) => wheelTilesRef.current[i]?.letter || "")
            .join("");
        selectedIndicesRef.current = [];
        setSelectedIndices([]);
        if (normalizeWord(word).length >= 3) submitWord(word);
    }, [submitWord]);

    const handleCellClick = useCallback((row, col) => {
        const matches = findWordsAtCell(puzzle?.filledCoordinates, row, col, cellWordNumbers);
        if (matches.length === 0) return;
        const clicked = normalizeWord(matches[0].word);
        if (!foundWords.includes(clicked)) {
            setSelectedWord(clicked);
            return;
        }
        setSelectedWord(firstUnrevealedWord(targetWords, foundWords, wordToNumber) || clicked);
    }, [puzzle?.filledCoordinates, cellWordNumbers, foundWords, targetWords, wordToNumber]);

    const applyHintLetter = (pick) => {
        setHintLetters((prev) => {
            const next = new Map(prev);
            next.set(pick.key, pick.letter);
            return next;
        });
        setSelectedWord(pick.word);
        setHintHideUntil((until) => Math.max(until, failCountRef.current + CELL_HINT_AFTER_FAILS));
        setCellHintOpen(false);
    };

    const handleCoinHint = useCallback(async () => {
        if (puzzleComplete || hintCandidates.length === 0 || hintPending) return;
        const pick = hintCandidates[0];
        if (!pick) return;
        if (!signedIn || coins < WORD_WHEEL_COIN_HINT_COST) {
            setBuyPrompt(WORD_WHEEL_COINS_PLAN);
            return;
        }
        setHintPending(true);
        try {
            const spent = await WordWheelService.spendHintCoins({
                coins: WORD_WHEEL_COIN_HINT_COST,
                featureUsed: "word_wheel_letter_hint",
            });
            if (spent?.wordWheelCoins != null) setCoins(Number(spent.wordWheelCoins) || 0);
            else setCoins((n) => Math.max(0, n - WORD_WHEEL_COIN_HINT_COST));
            applyHintLetter(pick);
        } catch (e) {
            setError(e?.message || t("wordwheel.hint-failed"));
        } finally {
            setHintPending(false);
        }
    }, [puzzleComplete, hintCandidates, hintPending, signedIn, coins, navigate, location, t]);

    const handleBulbClick = useCallback(async () => {
        if (giftPending) {
            try {
                const gift = await collectWordWheelDailyGift();
                const granted = Number(gift?.granted) || giftGrant || 1;
                if (granted > 0) {
                    if (gift?.wordWheelCoins != null) {
                        setCoins(Number(gift.wordWheelCoins) || 0);
                    } else {
                        setCoins((n) => n + granted);
                    }
                    setGiftNote(t("wordwheel.gift-granted", { count: granted }));
                    setGiftSnackOpen(true);
                }
                setGiftPending(false);
                setGiftGrant(0);
            } catch (e) {
                setError(e?.message || t("wordwheel.gift-failed", "Could not collect today’s gift."));
            }
            return;
        }
        await handleCoinHint();
    }, [giftPending, giftGrant, handleCoinHint, t]);

    const setSelection = useCallback((updater) => {
        setSelectedIndices((prev) => {
            const next = typeof updater === "function" ? updater(prev) : updater;
            selectedIndicesRef.current = next;
            return next;
        });
    }, []);

    const lookupWord = dictionaryWord || selectedWord || "";
    const lookupRevealed = Boolean(
        lookupWord
        && (foundWords.includes(normalizeWord(lookupWord))
            || bonusWordsFound.includes(normalizeWord(lookupWord))),
    );
    const selectedWordPattern = useMemo(
        () => buildWordRevealLetters(selectedWord, wordPositions, displayGrid),
        [selectedWord, wordPositions, displayGrid],
    );
    const buyPromptAnchor = bulbBtnRef.current;

    const hubPath = "/puzzles/word-wheel";
    const levelNumber = Number(puzzle?.mainJourneyLevel ?? puzzle?.puzzleLevel) || getGuestJourneyLevel();
    const goPrevDaily = () => {
        if (!ownsDailyBundle) return;
        const current = puzzle?.dailyPlayDate || dailyDate;
        if (!current) return;
        navigate(`/puzzles/word-wheel/daily?date=${encodeURIComponent(shiftDateIso(current, -1))}`);
    };

    const goNextDaily = () => {
        const current = puzzle?.dailyPlayDate || dailyDate;
        if (!current) return;
        const next = shiftDateIso(current, 1);
        if (next > todayIso()) return;
        navigate(`/puzzles/word-wheel/daily?date=${encodeURIComponent(next)}`);
    };

    const handleContinue = () => {
        setCompletionDialogOpen(false);
        if (isDaily) {
            navigate("/puzzles/word-wheel");
            return;
        }
        if (packCode) {
            const nextLevel = (Number(levelParam) || Number(puzzle?.puzzleLevel) || 1) + 1;
            navigate(`/puzzles/word-wheel/pack/${packCode}/${nextLevel}`, { replace: true });
            return;
        }
        if (signedIn) {
            if (location.pathname === "/puzzles/word-wheel/journey") {
                setReloadNonce((n) => n + 1);
            } else {
                navigate("/puzzles/word-wheel/journey", { replace: true });
            }
            return;
        }
        navigate(`/puzzles/word-wheel/journey/${getGuestJourneyLevel()}`, { replace: true });
    };

    const toolbarButtonSx = {
        width: 44,
        height: 44,
        bgcolor: "rgba(255, 255, 255, 0.88)",
        border: `1px solid ${playColors.borderStrong}`,
        color: "#0c4a6e",
        "&:hover": { bgcolor: "rgba(255, 255, 255, 0.96)" },
        "&.Mui-disabled": { opacity: 0.45 },
    };

    if (loading) {
        return (
            <Box sx={{
                position: "relative",
                minHeight: "70vh",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                overflow: "hidden",
                bgcolor: "#031525",
            }}>
                <CircularProgress sx={{ position: "relative", zIndex: 1, color: playColors.accent }} />
            </Box>
        );
    }

    return (
        <Box sx={{
            position: "relative",
            minHeight: "100%",
            overflow: "hidden",
            color: playColors.text,
            py: 2,
            px: { xs: 1.5, sm: 2 },
            bgcolor: "#031525",
        }}>
            <Paper
                elevation={8}
                sx={{
                    position: "relative",
                    zIndex: 1,
                    maxWidth: landscapePlay ? 1100 : 720,
                    mx: "auto",
                    p: { xs: 1.5, sm: 2.5 },
                    borderRadius: 3,
                    bgcolor: "rgba(8, 47, 73, 0.12)",
                    border: "1px solid rgba(186, 230, 253, 0.55)",
                    boxShadow: "0 12px 40px rgba(3, 21, 37, 0.38)",
                    overflow: "visible",
                }}
            >
                <Box
                    aria-hidden
                    sx={{
                        position: "absolute",
                        inset: 0,
                        borderRadius: "inherit",
                        overflow: "hidden",
                        zIndex: 0,
                        pointerEvents: "none",
                    }}
                >
                    <Box
                        sx={{
                            position: "absolute",
                            inset: 0,
                            backgroundImage: `url("${JOURNEY_APP_BACKGROUND_URL}")`,
                            backgroundSize: "cover",
                            backgroundPosition: "center",
                            backgroundRepeat: "no-repeat",
                            opacity: 0.42,
                        }}
                    />
                    <Box
                        sx={{
                            position: "absolute",
                            inset: 0,
                            bgcolor: "rgba(3, 21, 37, 0.58)",
                        }}
                    />
                </Box>
                <Box sx={{ position: "relative", zIndex: 1 }}>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                    <IconButton component={RouterLink} to={hubPath} aria-label={t("wordwheel.back")} sx={{ color: playColors.text, minWidth: 44, minHeight: 44 }}>
                        <ArrowBackIcon />
                    </IconButton>
                    <Box sx={{ flex: 1, textAlign: "center", minWidth: 0 }}>
                        <Typography variant="overline" sx={{ letterSpacing: 2, fontWeight: 700 }}>
                            {packCode
                                ? (wordWheelPackOffer(packCode)?.name || t("wordwheel.journey"))
                                : (isDaily ? t("wordwheel.daily") : t("wordwheel.journey"))}
                        </Typography>
                        {isDaily ? (
                            <Stack direction="row" alignItems="center" justifyContent="center" spacing={0.5}>
                                <IconButton
                                    onClick={goPrevDaily}
                                    disabled={!ownsDailyBundle}
                                    aria-label={t("wordwheel.previous-day")}
                                    sx={{
                                        color: playColors.text,
                                        minWidth: 44,
                                        minHeight: 44,
                                        "&.Mui-disabled": { color: "rgba(240, 249, 255, 0.35)" },
                                    }}
                                >
                                    <ChevronLeftIcon />
                                </IconButton>
                                <Typography variant="h6" sx={{ fontWeight: 800, minWidth: "11ch" }}>
                                    {puzzle?.dailyPlayDate || dailyDate || t("wordwheel.daily")}
                                </Typography>
                                <IconButton
                                    onClick={goNextDaily}
                                    disabled={(puzzle?.dailyPlayDate || dailyDate || todayIso()) >= todayIso()}
                                    aria-label={t("wordwheel.next-day")}
                                    sx={{
                                        color: playColors.text,
                                        minWidth: 44,
                                        minHeight: 44,
                                        "&.Mui-disabled": { color: "rgba(240, 249, 255, 0.35)" },
                                    }}
                                >
                                    <ChevronRightIcon />
                                </IconButton>
                            </Stack>
                        ) : (
                            <Typography variant="h6" sx={{ fontWeight: 800 }}>
                                {t("wordwheel.journey-level", { level: levelNumber })}
                            </Typography>
                        )}
                    </Box>
                    <Box sx={{ width: 44 }} />
                </Stack>

                <WordWheelWallet
                    compact
                    openShopKind={openShopKind}
                    onOpenShopKindConsumed={() => setOpenShopKind(null)}
                />

                {!signedIn ? (
                    <Alert
                        severity="info"
                        sx={{ my: 1.5 }}
                        action={
                            <Button color="inherit" size="small" component={RouterLink} to="/login" state={{ from: { pathname: `${location.pathname}${location.search || ""}` } }}>
                                {t("wordwheel.join")}
                            </Button>
                        }
                    >
                        {t("wordwheel.guest-banner")}
                    </Alert>
                ) : null}

                {error ? <Alert severity="error" sx={{ my: 1 }}>{error}</Alert> : null}

                <Box
                    sx={{
                        display: "flex",
                        flexDirection: landscapePlay ? "row" : "column",
                        alignItems: landscapePlay ? "stretch" : "stretch",
                        gap: landscapePlay ? 2 : 0,
                        mt: 0.5,
                        minHeight: 0,
                    }}
                >
                <Box
                    sx={{
                        flex: landscapePlay ? "1 1 0" : "none",
                        minWidth: 0,
                        minHeight: 0,
                        width: landscapePlay ? "auto" : "100%",
                        maxWidth: landscapePlay ? "none" : 480,
                        mx: landscapePlay ? 0 : "auto",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        my: landscapePlay ? 0 : 1.5,
                        p: 0.75,
                        overflow: "visible",
                    }}
                >
                <Box
                    sx={{
                        width: landscapePlay ? "min(100%, 68vh)" : "100%",
                        maxWidth: "100%",
                        aspectRatio: "1 / 1",
                        maxHeight: landscapePlay ? "68vh" : "none",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        overflow: "visible",
                    }}
                >
                    <Box
                        sx={{
                            width: `${(visibleCols / gridSize) * 100}%`,
                            height: `${(visibleRows / gridSize) * 100}%`,
                            display: "grid",
                            gridTemplateColumns: `repeat(${visibleCols}, 1fr)`,
                            gridTemplateRows: `repeat(${visibleRows}, 1fr)`,
                            gap: "4px",
                            overflow: "visible",
                        }}
                    >
                    {Array.from({ length: visibleRows }, (_, rowOffset) =>
                        Array.from({ length: visibleCols }, (_, colOffset) => {
                            const row = gridBounds.minRow + rowOffset;
                            const col = gridBounds.minCol + colOffset;
                            const letter = displayGrid[row]?.[col] || "";
                            const cellKey = `${row},${col}`;
                            const isPuzzleCell = puzzleCells.has(cellKey);
                            const isRevealed = Boolean(letter);
                            const isHintRevealed = isRevealed && hintOnlyCells.has(cellKey);
                            const isSelected = selectedWordCells.has(cellKey);
                            const wordNumber = cellWordNumbers.get(`${row},${col}`);
                            return (
                                <Box
                                    key={`${row}-${col}`}
                                    onClick={isPuzzleCell ? () => handleCellClick(row, col) : undefined}
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        aspectRatio: "1 / 1",
                                        position: "relative",
                                        fontSize: "clamp(10px, 3.2vmin, 18px)",
                                        fontWeight: 700,
                                        borderRadius: 1.25,
                                        cursor: isPuzzleCell ? "pointer" : "default",
                                        bgcolor: !isPuzzleCell
                                            ? "transparent"
                                            : isRevealed
                                                ? isHintRevealed
                                                    ? "rgba(254, 243, 199, 0.95)"
                                                    : playColors.successSoft
                                                : playColors.gridCellHidden,
                                        color: isRevealed
                                            ? isHintRevealed
                                                ? "#92400e"
                                                : playColors.successText
                                            : "transparent",
                                        border: isPuzzleCell
                                            ? `2px solid ${
                                                isSelected
                                                    ? playColors.accent
                                                    : isRevealed
                                                        ? isHintRevealed
                                                            ? "#fcd34d"
                                                            : "#bbf7d0"
                                                        : playColors.borderStrong
                                            }`
                                            : "none",
                                        minWidth: 0,
                                        minHeight: 0,
                                        overflow: "visible",
                                    }}
                                >
                                    {wordNumber && isPuzzleCell ? (
                                        <Box
                                            component="span"
                                            sx={{
                                                position: "absolute",
                                                top: 0,
                                                left: 0,
                                                transform: "translate(-45%, -45%)",
                                                display: "inline-flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                boxSizing: "border-box",
                                                width: "1.4em",
                                                height: "1.4em",
                                                p: 0,
                                                fontSize: "clamp(10px, 2.8vmin, 13px)",
                                                fontWeight: 800,
                                                lineHeight: 1,
                                                fontVariantNumeric: "tabular-nums",
                                                color: "#064e3b",
                                                bgcolor: "rgba(255, 255, 255, 0.96)",
                                                borderRadius: "4px",
                                                zIndex: 3,
                                                boxShadow: "0 1px 3px rgba(3, 21, 37, 0.28)",
                                                pointerEvents: "none",
                                            }}
                                        >
                                            {wordNumber}
                                        </Box>
                                    ) : null}
                                    <Box component="span" sx={{ color: "inherit" }}>{letter}</Box>
                                    {cellHintKeys.has(cellKey) ? (
                                        <Box
                                            ref={cellHintBtnRef}
                                            component="button"
                                            type="button"
                                            onClick={(event) => {
                                                event.stopPropagation();
                                                setCellHintOpen(true);
                                            }}
                                            aria-label={t("wordwheel.cell-hint")}
                                            sx={{
                                                position: "absolute",
                                                top: -7,
                                                right: -7,
                                                zIndex: 5,
                                                width: 24,
                                                height: 24,
                                                minWidth: 24,
                                                p: 0,
                                                m: 0,
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                borderRadius: "50%",
                                                bgcolor: "#C2410C",
                                                color: "#fff",
                                                border: "2px solid #fff",
                                                boxShadow: "0 2px 6px rgba(3, 21, 37, 0.35)",
                                                cursor: "pointer",
                                                animation: "wwCellHintPulse 1.4s ease-in-out infinite",
                                                "@keyframes wwCellHintPulse": {
                                                    "0%, 100%": { transform: "scale(1)" },
                                                    "50%": { transform: "scale(1.12)" },
                                                },
                                                "&:hover": { bgcolor: "#9A3412" },
                                            }}
                                        >
                                            <LightbulbOutlinedIcon sx={{ fontSize: 13, color: "#fff" }} />
                                        </Box>
                                    ) : null}
                                </Box>
                            );
                        }),
                    )}
                    </Box>
                </Box>
                </Box>

                <Box
                    sx={{
                        flex: landscapePlay ? "1 1 0" : "none",
                        minWidth: 0,
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: landscapePlay ? "center" : "flex-start",
                        gap: landscapePlay ? 1.5 : 0,
                    }}
                >
                <ClueBanner
                    clueText={
                        selectedWord
                            ? `${selectedWordNumber != null ? `${selectedWordNumber}. ` : ""}${selectedClue || t("wordwheel.no-clue")}`
                            : t(CLUE_PLACEHOLDER_KEY)
                    }
                    hasClue={Boolean(selectedWord)}
                    textColor={playColors.textOnSurface}
                    overlay={
                        selectedWheelWord ? (
                            <Box
                                sx={{
                                    position: "absolute",
                                    inset: 0,
                                    zIndex: 2,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    px: 1.5,
                                    borderRadius: 2,
                                    bgcolor: "rgba(255, 251, 235, 0.96)",
                                    pointerEvents: "none",
                                }}
                            >
                                <Typography
                                    aria-live="polite"
                                    sx={{
                                        fontWeight: 800,
                                        fontSize: "clamp(1.5rem, 7vw, 2.125rem)",
                                        letterSpacing: "0.14em",
                                        color: playColors.textOnSurface,
                                        textTransform: "uppercase",
                                        lineHeight: 1.1,
                                    }}
                                >
                                    {selectedWheelWord}
                                </Typography>
                            </Box>
                        ) : null
                    }
                />

                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        mt: landscapePlay ? 0 : 2,
                        gap: 1,
                        flex: landscapePlay ? "1 1 auto" : "none",
                        minHeight: 0,
                    }}
                >
                    <Stack spacing={1} sx={{ width: WHEEL_TOOLBAR_WIDTH, alignItems: "center" }}>
                        <Tooltip title={giftPending
                            ? t("wordwheel.gift-tap", "Daily visit gift — tap to collect")
                            : t("wordwheel.bulb-hint")}
                        >
                            <span>
                                <IconButton
                                    ref={bulbBtnRef}
                                    onClick={handleBulbClick}
                                    disabled={giftPending
                                        ? false
                                        : puzzleComplete || hintCandidates.length === 0 || hintPending}
                                    aria-label={giftPending
                                        ? t("wordwheel.gift-tap", "Daily visit gift — tap to collect")
                                        : `${t("wordwheel.bulb-hint")}. ${t("wordwheel.coins-label")}: ${coins}`}
                                    sx={{
                                        ...toolbarButtonSx,
                                        overflow: "visible",
                                        ...(giftPending
                                            ? {
                                                animation: "wwGiftBulbPulse 1.2s ease-in-out infinite",
                                                "@keyframes wwGiftBulbPulse": {
                                                    "0%, 100%": { transform: "scale(1)" },
                                                    "50%": { transform: "scale(1.08)" },
                                                },
                                            }
                                            : {}),
                                    }}
                                >
                                    <Badge
                                        badgeContent={coins}
                                        showZero
                                        overlap="circular"
                                        anchorOrigin={{ vertical: "top", horizontal: "right" }}
                                        sx={{
                                            "& .MuiBadge-badge": {
                                                bgcolor: "#facc15",
                                                color: "#78350f",
                                                fontWeight: 800,
                                                minWidth: 20,
                                                height: 20,
                                                fontSize: "0.72rem",
                                                border: "1px solid rgba(120, 53, 15, 0.35)",
                                                top: 2,
                                                right: 2,
                                                transform: "translate(70%, -70%)",
                                            },
                                        }}
                                    >
                                        <Box sx={{ position: "relative", display: "inline-flex" }}>
                                            <LightbulbOutlinedIcon />
                                            {giftPending ? (
                                                <CardGiftcardIcon
                                                    sx={{
                                                        position: "absolute",
                                                        top: -8,
                                                        left: -8,
                                                        fontSize: 16,
                                                        color: "#facc15",
                                                        filter: "drop-shadow(0 1px 3px rgba(0,0,0,0.55))",
                                                        animation: "wwGiftArrive 1.15s ease-in-out infinite",
                                                        "@keyframes wwGiftArrive": {
                                                            "0%, 100%": { transform: "translateY(0) scale(1)" },
                                                            "50%": { transform: "translateY(-2px) scale(1.14)" },
                                                        },
                                                    }}
                                                />
                                            ) : null}
                                        </Box>
                                    </Badge>
                                </IconButton>
                            </span>
                        </Tooltip>
                        <Tooltip title={t("wordwheel.treasure-a11y")}>
                            <span>
                                <IconButton
                                    onClick={() => setTreasureOpen(true)}
                                    aria-label={t("wordwheel.treasure-a11y")}
                                    sx={{
                                        ...toolbarButtonSx,
                                        ...(chestPulse
                                            ? {
                                                animation: "wwChestPulse 0.7s ease",
                                                "@keyframes wwChestPulse": {
                                                    "0%, 100%": { transform: "scale(1)" },
                                                    "45%": { transform: "scale(1.18)" },
                                                },
                                            }
                                            : {}),
                                    }}
                                >
                                    <Badge
                                        badgeContent={bonusWordsFound.length || null}
                                        color="warning"
                                        overlap="circular"
                                    >
                                        <TreasureChestIcon sx={{ color: "#0c4a6e" }} />
                                    </Badge>
                                </IconButton>
                            </span>
                        </Tooltip>
                    </Stack>
                    <Box
                        sx={{
                            flex: 1,
                            minWidth: 0,
                            maxWidth: landscapePlay ? 320 : 280,
                            height: landscapePlay
                                ? "min(56vh, 300px)"
                                : "min(72vw, 240px)",
                            aspectRatio: landscapePlay ? "1 / 1" : "auto",
                        }}
                    >
                        {wheelTiles.length > 0 ? (
                            <LetterWheel
                                tiles={wheelTiles}
                                selectedIndices={selectedIndices}
                                onSelectionChange={setSelection}
                                onDragEnd={handleDragEnd}
                                onShuffle={() => setWheelTiles((prev) => shuffleWheelTiles(prev))}
                                shuffleSignal={shuffleSignal}
                                colors={playColors}
                                fill
                            />
                        ) : (
                            <Typography sx={{ textAlign: "center", color: playColors.textMuted }}>
                                {t("wordwheel.no-letters")}
                            </Typography>
                        )}
                    </Box>
                    <Stack spacing={1} sx={{ width: WHEEL_TOOLBAR_WIDTH, alignItems: "center" }}>
                        <Tooltip title={t("wordwheel.dictionary")}>
                            <span>
                                <IconButton
                                    onClick={() => {
                                        setDictionaryWord(selectedWord || "");
                                        setDictionaryOpen(true);
                                    }}
                                    disabled={!selectedWord}
                                    aria-label={t("wordwheel.dictionary")}
                                    sx={toolbarButtonSx}
                                >
                                    <MenuBookIcon />
                                </IconButton>
                            </span>
                        </Tooltip>
                        <Tooltip title={t("wordwheel.shuffle")}>
                            <span>
                                <IconButton
                                    onClick={() => setShuffleSignal((n) => n + 1)}
                                    disabled={wheelTiles.length < 2}
                                    aria-label={t("wordwheel.shuffle")}
                                    sx={toolbarButtonSx}
                                >
                                    <ShuffleIcon />
                                </IconButton>
                            </span>
                        </Tooltip>
                    </Stack>
                </Box>
                </Box>
                </Box>
                </Box>
            </Paper>

            <Popover
                open={Boolean(buyPrompt)}
                anchorEl={buyPromptAnchor}
                onClose={() => setBuyPrompt(null)}
                anchorOrigin={{ vertical: "center", horizontal: "right" }}
                transformOrigin={{ vertical: "center", horizontal: "left" }}
                slotProps={{
                    paper: { sx: goldShopPaperSx },
                }}
            >
                <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center" }}>
                    <ShopHeroIcon>
                        <MonetizationOnIcon sx={{ fontSize: 52, color: "#d97706" }} />
                    </ShopHeroIcon>
                    <Typography sx={{ fontWeight: 800, fontSize: "1.15rem", lineHeight: 1.3, mb: 0.5 }}>
                        {t("wordwheel.need-coins")}
                    </Typography>
                    <Typography sx={{ fontWeight: 600, mb: 1.5, opacity: 0.85 }}>
                        {t("wordwheel.need-coins-hint", { count: WORD_WHEEL_COIN_HINT_COST })}
                    </Typography>
                    <Button
                        variant="contained"
                        fullWidth
                        startIcon={<MonetizationOnIcon />}
                        sx={goldCtaSx}
                        onClick={() => {
                            setOpenShopKind("coins");
                            setBuyPrompt(null);
                        }}
                    >
                        {t("wordwheel.buy-coins")}
                    </Button>
                </Box>
            </Popover>

            <Popover
                open={cellHintOpen}
                anchorEl={cellHintBtnRef.current}
                onClose={() => setCellHintOpen(false)}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                transformOrigin={{ vertical: "top", horizontal: "right" }}
                slotProps={{
                    paper: { sx: goldShopPaperSx },
                }}
            >
                <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center" }}>
                    <ShopHeroIcon>
                        <MonetizationOnIcon sx={{ fontSize: 52, color: "#d97706" }} />
                    </ShopHeroIcon>
                    <Typography sx={{ fontWeight: 800, fontSize: "1.15rem", lineHeight: 1.3, mb: 0.75 }}>
                        {t("wordwheel.cell-hint-copy")}
                    </Typography>
                    <Button
                        variant="contained"
                        fullWidth
                        startIcon={<MonetizationOnIcon />}
                        onClick={() => {
                            setOpenShopKind("coins");
                            setCellHintOpen(false);
                            setHintHideUntil(failCountRef.current + CELL_HINT_AFTER_FAILS);
                        }}
                        sx={{ ...goldCtaSx, mt: 1.25 }}
                    >
                        {t("wordwheel.cell-hint-cta")}
                    </Button>
                </Box>
            </Popover>

            <WordMeaningDialog
                open={dictionaryOpen}
                onClose={() => {
                    setDictionaryOpen(false);
                    setDictionaryWord("");
                }}
                word={lookupWord}
                hideWord={!lookupRevealed}
                titleContent={
                    selectedWord && lookupWord === selectedWord
                        ? <WordRevealCircles letters={selectedWordPattern} />
                        : null
                }
                language={puzzle?.language || "english"}
                uiLang={lang}
            />

            <TreasureBonusWordsDialog
                open={treasureOpen}
                onClose={() => setTreasureOpen(false)}
                words={bonusWordsFound}
                giftCoins={WORD_WHEEL_BONUS_WORD_GIFT}
                onWordPress={(word) => {
                    setTreasureOpen(false);
                    setDictionaryWord(word);
                    setDictionaryOpen(true);
                }}
            />

            <WordWheelCompleteDialog
                open={completionDialogOpen}
                onClose={() => setCompletionDialogOpen(false)}
                onContinue={handleContinue}
                isDaily={isDaily}
            />

            <Snackbar
                open={giftSnackOpen}
                autoHideDuration={5000}
                onClose={() => setGiftSnackOpen(false)}
                anchorOrigin={{ vertical: "top", horizontal: "center" }}
                message={giftNote || t("wordwheel.gift-granted", { count: giftGrant || 1 })}
            />
        </Box>
    );
}
